import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { AuthProvider } from '../application/account/AuthProvider';
import { GardenProvider } from '../application/plants/useGarden';
import { LocaleProvider } from '../application/i18n/LocaleProvider';
import { applyLocaleDocumentMetadata } from '../application/i18n/localeDocumentMetadata';
import { readBrowserLanguages, readStoredLocale } from '../application/i18n/localePreference';
import { isSupportedLocale, splitLocalePath, stripLocaleLikePrefix } from '../domain/i18n/locales';
import { negotiateLocale } from '../domain/i18n/negotiateLocale';
import { translate } from '../domain/i18n/messages';
import { useAutomaticAppUpdate } from '../application/pwa/useAutomaticAppUpdate';
import AppErrorBoundary from '../ui/chrome/AppErrorBoundary';
import BottomNavigation from '../ui/chrome/BottomNavigation';
import AppHeader from '../ui/chrome/AppHeader';
import GardenScreen from '../ui/screens/GardenScreen';
import LandingScreen from '../ui/screens/LandingScreen';
import PlantDetailScreen from '../ui/screens/PlantDetailScreen';
import PlantsScreen from '../ui/screens/PlantsScreen';
import AppOffers from '../ui/pwa/AppOffers';
import PlantArrivalWatch from '../ui/pwa/PlantArrivalWatch';
import { ROUTE_PATHS, chromeForRoute } from './routes';

/**
 * Send a visitor who arrived without a locale to the one we negotiated for them.
 * `replace` so the un-prefixed URL does not become a back-button trap.
 */
function LocaleRedirect() {
    const { pathname, search, hash } = useLocation();
    // Strips a locale-SHAPED prefix, so an unlaunched market such as `/fr-fr/plants`
    // keeps `/plants` on the way to the locale we negotiate for it.
    const routePath = stripLocaleLikePrefix(pathname);
    const localeCode = negotiateLocale({
        storedLocale: readStoredLocale(),
        browserLanguages: readBrowserLanguages(),
    });
    const normalizedRoute = routePath === '/' ? '' : routePath.replace(/\/$/u, '');

    return <Navigate to={`/${localeCode}${normalizedRoute}${search}${hash}`} replace />;
}

/**
 * Everything beneath `/:locale`. An unsupported locale segment is not a 404 —
 * it is most likely a market we have not launched yet, so it re-negotiates
 * rather than dead-ends.
 */
function LocalisedShell() {
    const { locale } = useParams();
    const { pathname } = useLocation();

    useEffect(() => {
        if (!isSupportedLocale(locale)) {
            return;
        }

        applyLocaleDocumentMetadata({ localeCode: locale, routePath: splitLocalePath(pathname).routePath });
    }, [locale, pathname]);

    if (!isSupportedLocale(locale)) {
        return <LocaleRedirect />;
    }

    return (
        <LocaleProvider localeCode={locale}>
            {/* Both above the routes: ONE auth subscription and ONE garden sync
                engine for the whole shell, however many screens read them
                (SPEC-022). Inside the locale provider because the drawer's
                account section speaks. */}
            <AuthProvider>
                <GardenProvider>
                    <LocalisedChrome locale={locale} />
                </GardenProvider>
            </AuthProvider>
        </LocaleProvider>
    );
}

function LocalisedChrome({ locale }) {
    const { pathname } = useLocation();
    // Not every screen wears the same chrome: the landing is a marketing front
    // door with no tab bar, and a plant detail page is immersive with neither.
    const chrome = chromeForRoute(splitLocalePath(pathname).routePath);

    return (
        <>
            {/* Renders nothing, and sits above the routes so browsing to a plant
                and back does not restart the catalogue watch (SPEC-016). */}
            <PlantArrivalWatch />
            <div className={`app-shell${chrome.immersive ? ' app-shell--immersive' : ''}`}>
                <div className="app-shell__content">
                    {chrome.header ? <AppHeader mark={chrome.brandMark} /> : null}
                    <Routes>
                        <Route index element={<LandingScreen />} />
                        <Route path="plants" element={<PlantsScreen />} />
                        <Route path="plants/:plantId" element={<PlantDetailScreen />} />
                        <Route path="garden" element={<GardenScreen />} />
                        <Route path="*" element={<Navigate to={`/${locale}${ROUTE_PATHS.home}`} replace />} />
                    </Routes>
                    {/* At the foot of the page and inside the scroll, on the
                        tabbed screens only: the landing is a front door with its
                        own single call to action, and a plant detail page is
                        immersive with an action pinned to its foot already. */}
                    {chrome.tabBar ? <AppOffers /> : null}
                </div>
                {chrome.tabBar ? <BottomNavigation /> : null}
            </div>
        </>
    );
}

export default function AppShell() {
    // The boundary sits above the locale provider, so its copy is resolved
    // against the browser's best-guess language rather than the app's.
    const fallbackLanguage = negotiateLocale({ browserLanguages: readBrowserLanguages() }).split('-')[1];

    // Above the routes, so the watch outlives every navigation and a locale
    // switch never restarts the idle clock (SPEC-010).
    useAutomaticAppUpdate();

    return (
        <AppErrorBoundary
            title={translate(fallbackLanguage, 'error.title')}
            body={translate(fallbackLanguage, 'error.body')}
        >
            <Routes>
                <Route path="/:locale/*" element={<LocalisedShell />} />
                <Route path="*" element={<LocaleRedirect />} />
            </Routes>
        </AppErrorBoundary>
    );
}
